import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Zap, Users, Target, Compass, Sparkles } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const ideas = [
  {
    title: 'Use What You Got and Make a Lot',
    icon: Zap,
    description: 'A philosophy of practical entrepreneurship based on leveraging available resources to build meaningful ventures. Success isn\'t about waiting for perfect conditions—it\'s about maximizing what you have right now.',
    principles: [
      'Start with what\'s available, not what\'s ideal',
      'Resourcefulness beats resources',
      'Action creates opportunity',
      'Constraints breed creativity'
    ],
    quote: 'The entrepreneur who waits for perfect conditions never builds anything. The one who works with what they have changes the world.',
    color: '#D4AF37'
  },
  {
    title: 'NFQ — Never FN Quit',
    icon: Target,
    description: 'A mindset framework emphasizing perseverance, resilience, and the willingness to continue learning and building despite obstacles. Not about stubbornness—about intelligent persistence.',
    principles: [
      'Failure is feedback, not finality',
      'Persistence with adaptation',
      'Learn from every setback',
      'The only real failure is giving up'
    ],
    quote: 'Every successful venture has a story of near-failure. The difference is they didn\'t stop when it got hard.',
    color: '#EF4444'
  },
  {
    title: 'Human + AI Collaboration',
    icon: Users,
    description: 'The belief that artificial intelligence should augment human creativity and insight rather than replace it. Technology at its best expands human capability.',
    principles: [
      'AI amplifies, humans direct',
      'Preserve human agency',
      'Technology serves people',
      'Creativity remains human'
    ],
    quote: 'The question isn\'t whether AI will be powerful—it\'s whether we\'ll use that power to expand human potential or diminish it.',
    color: '#3B82F6'
  },
  {
    title: 'Digital Legacy Preservation',
    icon: Sparkles,
    description: 'The importance of capturing and preserving human stories, wisdom, and experience for future generations. In a digital age, legacies can endure forever—if we build the systems to preserve them.',
    principles: [
      'Every life has stories worth preserving',
      'Digital tools enable permanent memory',
      'Legacy is connection across time',
      'Stories define who we are'
    ],
    quote: 'Our ancestors left cave paintings. We can leave something far richer—if we choose to.',
    color: '#8B5CF6'
  },
  {
    title: 'Systems Thinking',
    icon: Compass,
    description: 'Understanding problems and opportunities through the lens of interconnected systems. Complex challenges require seeing patterns, relationships, and leverage points.',
    principles: [
      'Everything connects to everything',
      'Find the leverage points',
      'Small changes, big effects',
      'See the whole, not just parts'
    ],
    quote: 'The best solutions come from understanding the system, not just the symptoms.',
    color: '#10B981'
  },
  {
    title: 'Entrepreneurial Agency',
    icon: Lightbulb,
    description: 'The fundamental belief that individuals have the power to create, build, and shape their circumstances. Entrepreneurship as a mindset, not just a career path.',
    principles: [
      'You can build what doesn\'t exist',
      'Circumstances can be changed',
      'Initiative creates opportunity',
      'Agency is a choice'
    ],
    quote: 'The world needs more builders—people who see problems as opportunities to create solutions.',
    color: '#F59E0B'
  }
];

export default function IdeasPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl">
          <motion.span
            className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Philosophy
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="ideas-headline"
          >
            Ideas & Principles
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            The intellectual frameworks and philosophies that guide the work. These ideas have been developed and refined over decades of building, failing, learning, and building again.
          </motion.p>
        </div>
      </Section>

      {/* Ideas Detail */}
      {ideas.map((idea, index) => (
        <Section key={idea.title} dark={index % 2 === 1}>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              className={index % 2 === 1 ? 'lg:order-2' : ''}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${idea.color}20` }}
                >
                  <idea.icon className="w-7 h-7" style={{ color: idea.color }} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{idea.title}</h2>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {idea.description}
              </p>

              <blockquote 
                className="border-l-4 pl-6 text-xl text-slate-300 italic mb-8"
                style={{ borderColor: idea.color }}
              >
                "{idea.quote}"
              </blockquote>
            </motion.div>

            <motion.div
              className={index % 2 === 1 ? 'lg:order-1' : ''}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-card border border-border rounded-xl p-8">
                <h3 className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: idea.color }}>
                  Core Principles
                </h3>
                <ul className="space-y-4">
                  {idea.principles.map((principle, i) => (
                    <motion.li
                      key={principle}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: idea.color }}
                      />
                      <span className="text-slate-300">{principle}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </Section>
      ))}

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ideas in Action
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            These philosophies aren't abstract concepts—they're actively applied in ventures, books, and consulting work. Explore how ideas become reality.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="explore-ventures-btn">
              <Link to="/ventures">Explore Ventures <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="read-books-btn">
              <Link to="/books">Read the Books <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
